import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getCollateralRequested,
  setCollateralFilterDate,
} from "../../../redux/actions/collaterals";
import { convertAmountToFrontend } from "../../../helpers/convert";
import Button from "../../../components/Button";
import { Input } from "../../../components/Inputs";
import EmptyState from "../../../components/EmptyState/EmptyState";
import Loading from "../../../components/Loading";
import moment from "moment";
import ComboBox from "../../../components/ComboBox";
import { API_EXPORT } from "../../../helpers/api";
import fileDownload from "js-file-download";
import { setNotification } from "../../../helpers/notification";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";

export default function Collateral({ t }) {
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [activePage, setActivePage] = useState(0);
  const [sortedColumn, setSortedColumn] = useState("productionPeriod");
  const [sortDirection, setSortDirection] = useState("desc");
  // Filters
  let selectedOrganization = useSelector(
    (state) => state.organizations.selectedOrganization
  );
  const collaterals = useSelector((state) => state.collaterals.activeList);

  const loading = useSelector((state) => state.collaterals.loading);

  const activeCollateralsColumns = [
    {
      name: t("pages.default.collateral.table.etsoCode"),
      selector: "etsoCode",
      sortable: true,
    },
    {
      name: t("pages.default.collateral.table.organizationName"),
      selector: "organizationName",
      sortable: true,
    },
    {
      name: t("pages.default.collateral.table.collateralDate"),
      selector: "date",
      sortable: true,
      cell: (row) => {
        return moment(row.date).format("DD.MM.YYYY HH:mm");
      },
    },
    {
      name: t("pages.default.collateral.table.totalDeposit"),
      selector: "totalDeposit",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.totalDeposit);
      },
    },
    {
      name: t("pages.default.collateral.table.deposit"),
      selector: "deposit",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.deposit);
      },
    },
  ];

  useEffect(() => {
    filterCollaterals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterCollaterals = () => {
    setStartDate(startDate ? startDate : null);
    setEndDate(endDate ? endDate : null);

    if (selectedOrganization && Object.keys(selectedOrganization).length === 0)
      selectedOrganization = undefined;
    let params = {
      startDate: startDate ? moment(startDate).format("DD-MM-YYYY") : null,
      endDate: endDate ? moment(endDate).format("DD-MM-YYYY") : null,
      organizationId: selectedOrganization
        ? selectedOrganization.organizationId
        : null,
    };

    try {
      if (startDate || endDate || selectedOrganization) {
        dispatch(
          setCollateralFilterDate({
            ...params,
          })
        );
      }
      dispatch(
        getCollateralRequested({
          ...params,
        })
      );
    } catch (e) {}
  };

  const downloadCollaterals = async () => {
    let params = {
      startDate: startDate ? moment(startDate).format("DD-MM-YYYY") : null,
      endDate: endDate ? moment(endDate).format("DD-MM-YYYY") : null,
      organizationId: selectedOrganization.organizationId ?? null,
    };
    setExportLoading(true);
    try {
      const res = await API_EXPORT("collateral/export/excel", {
        ...params,
        lang: window.localStorage.i18nextLng,
      });
      const [, filename] =
        res?.headers["content-disposition"].split("filename=");
      fileDownload(res.data, filename);
      setExportLoading(false);
      setNotification("success", t("ui.default.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setExportLoading(false);
    }
  };
  const handleClick = (activePage, selector, direction) => {
    console.log("page", activePage, selector, direction);
    setActivePage(activePage);
    let params = {
      startDate: startDate ? moment(startDate).format("DD-MM-YYYY") : null,
      endDate: endDate ? moment(endDate).format("DD-MM-YYYY") : null,
      organizationId: selectedOrganization
        ? selectedOrganization.organizationId
        : null,
    };
    dispatch(
      getCollateralRequested({
        ...params,
        page: activePage,
        selector,
        direction,
      })
    );
  };
  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.collateral.activeTitle")}
      </h5>
      <div className="km__p--card">
        <div className="km__p--certificates-filter">
          <ComboBox t={t} />
          <Input
            type="date"
            placeholder={t("pages.default.collateral.input.startDate")}
            max={
              new Date(moment(endDate).subtract(1, "days").format("MM-DD-YYYY"))
            }
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            id="ba-history-startDate"
          />
          <Input
            type="date"
            placeholder={t("pages.default.collateral.input.endDate")}
            min={
              new Date(moment(startDate).add(1, "days").format("MM-DD-YYYY"))
            }
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            id="ba-history-endDate"
          />
          <Button
            type="primary"
            label={t("ui.default.filter")}
            onClick={filterCollaterals}
            id="c-filter"
          />
          <Button
            type="secondary"
            label={t("ui.default.download")}
            suffixIcon="download"
            onClick={downloadCollaterals}
            disabled={exportLoading}
            id="c-download"
          />
        </div>
        {collaterals?.items && collaterals?.items.length > 0 && !loading && (
          <DataTableServerSideRendering
            columns={activeCollateralsColumns}
            data={collaterals?.items}
            rowKey="id"
            sortedColumn={sortedColumn}
            setSortedColumn={setSortedColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            pagination
            activePage={activePage}
            totalPageCount={collaterals?.total?.totalPageCount}
            handleFetchData={handleClick}
          />
        )}
        {collaterals.length === 0 && !loading && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.collateral.activeNotFound")}
          />
        )}
        {loading && (
          <Loading text={t("pages.default.collateral.activeLoading")} />
        )}
      </div>
    </>
  );
}
